import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { Phone, ChevronRight, MapPin, Clock, Star, Check, Truck, Wrench, Package, Repeat } from 'lucide-react';
import { SEOHead, getServiceSchema, getBreadcrumbSchema, SEO_BASE } from './SEO';

const AREA_DATA = {
  'orangevale': { name: 'Orangevale', zip: '95662', desc: 'As our home base, Orangevale receives our fastest response times. A2B Towing has been proudly serving the Orangevale community for over 10 years.', landmarks: 'Orangevale Community Park, Highway 50, Hazel Avenue' },
  'citrus-heights': { name: 'Citrus Heights', zip: '95610', desc: 'Located just minutes from our base, Citrus Heights residents enjoy quick towing and roadside assistance. We regularly serve the Sunrise Mall area and surrounding neighborhoods.', landmarks: 'Sunrise Mall, Greenback Lane, Auburn Blvd' },
  'el-dorado-hills': { name: 'El Dorado Hills', zip: '95762', desc: 'We provide premium towing services to El Dorado Hills, including flatbed towing for luxury vehicles common in this upscale community.', landmarks: 'El Dorado Hills Town Center, Highway 50, Serrano' },
  'folsom': { name: 'Folsom', zip: '95630', desc: 'Folsom residents trust A2B Towing for fast, reliable service. We cover Folsom Lake areas, Historic Folsom, and the Palladio shopping district.', landmarks: 'Folsom Lake, Historic Folsom, Palladio, Highway 50' },
  'granite-bay': { name: 'Granite Bay', zip: '95746', desc: 'Granite Bay homeowners appreciate our careful handling of luxury and classic vehicles. Our flatbed service is popular in this area.', landmarks: 'Folsom Lake, Douglas Blvd, Granite Bay Golf Club' },
  'rancho-cordova': { name: 'Rancho Cordova', zip: '95670', desc: 'We provide full towing and roadside assistance throughout Rancho Cordova, including the business districts along Folsom Blvd and Highway 50 corridor.', landmarks: 'Highway 50, Folsom Blvd, Mather Field' },
  'rocklin': { name: 'Rocklin', zip: '95677', desc: 'Rocklin residents and businesses rely on A2B Towing for dependable service. We cover the entire Rocklin area including the I-80 corridor.', landmarks: 'Sierra College, Quarry Park, I-80 Corridor' },
  'roseville': { name: 'Roseville', zip: '95678', desc: 'As one of the fastest-growing cities in Sacramento County, Roseville is a key service area. We cover Galleria Mall, Downtown Roseville, and all surrounding neighborhoods.', landmarks: 'Westfield Galleria, Downtown Roseville, I-80' },
};

function OtherAreas({ currentArea }) {
  const otherAreas = useMemo(
    () => SEO_BASE.serviceAreas.filter(a => a !== currentArea),
    [currentArea] // SEO_BASE is a module-level constant, a is callback param
  );
  return (
    <div className="flex flex-wrap gap-3 justify-center">
      {otherAreas.map(a => (
        <Link key={a} to={`/service-area/${a.toLowerCase().replace(/\s+/g, '-')}`} className="bg-zinc-800 text-gray-300 hover:text-white hover:bg-zinc-700 px-4 py-2 rounded-md text-sm transition-all">
          {a}
        </Link>
      ))}
    </div>
  );
}

function ServiceAreaPage() {
  const { areaSlug } = useParams();
  const area = AREA_DATA[areaSlug];

  if (!area) {
    return (
      <div className="min-h-screen bg-black pt-32 text-center">
        <h1 className="text-4xl font-bold text-white">Area Not Found</h1>
        <Link to="/" className="text-red-600 mt-4 inline-block">Go Home</Link>
      </div>
    );
  }

  const schema = [
    getBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/" }, { name: area.name, path: `/service-area/${areaSlug}` }]),
    getServiceSchema(`Towing Service in ${area.name}, CA`, `Professional towing and roadside assistance in ${area.name}, CA. Fast response, flatbed towing, jump starts. Call (916) 234-3923`)
  ];

  return (
    <div className="min-h-screen bg-black pt-16" data-testid="service-area-page">
      <SEOHead
        title={`Towing Service ${area.name} CA - Roadside Assistance | A2B Towing`}
        description={`Professional towing & roadside assistance in ${area.name}, CA. Fast response times, flatbed towing, jump starts, tire changes. Call (916) 234-3923. Serving ${area.name} ${area.zip}.`}
        canonical={`/service-area/${areaSlug}`}
        schema={schema}
      />

      <section className="pt-16 pb-20 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full">
              <span className="text-red-600 text-sm font-semibold">Service Area</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-testid="area-heading">
              Towing Service in <span className="text-red-600">{area.name}, CA</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">{area.desc}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+19162343923"><button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call (916) 234-3923</button></a>
              <Link to="/quote"><button className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 rounded-md font-semibold transition-all inline-flex items-center gap-2">Get Free Quote <ChevronRight className="w-4 h-4" /></button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <Clock className="w-8 h-8 text-red-600 mx-auto mb-3" />
              <h3 className="text-white font-bold mb-2">Fast Response</h3>
              <p className="text-gray-400 text-sm">30-45 min average response to {area.name}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <MapPin className="w-8 h-8 text-red-600 mx-auto mb-3" />
              <h3 className="text-white font-bold mb-2">Local Coverage</h3>
              <p className="text-gray-400 text-sm">Full coverage of {area.name} {area.zip}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <Star className="w-8 h-8 text-red-600 mx-auto mb-3" />
              <h3 className="text-white font-bold mb-2">5-Star Rated</h3>
              <p className="text-gray-400 text-sm">Trusted by {area.name} residents</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-8 text-center">Services Available in {area.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Truck, title: "Towing", items: ["Car & Truck Towing", "Motorcycle Towing", "Flatbed Service"] },
              { icon: Wrench, title: "Roadside", items: ["Jump Starts", "Tire Changes", "Fuel Delivery"] },
              { icon: Package, title: "Hauling", items: ["Equipment Hauling", "Vehicle Transport", "Appliances"] },
              { icon: Repeat, title: "Load Transfer", items: ["Safe Transfer", "Cargo Handling", "Professional"] },
            ].map(svc => (
              <div key={svc.title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <svc.icon className="w-8 h-8 text-red-600 mb-3" />
                <h3 className="text-white font-bold mb-3">{svc.title}</h3>
                {svc.items.map(item => (
                  <div key={item} className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                    <Check className="w-3 h-3 text-red-600" /> {item}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 mb-12">
            <h3 className="text-xl font-bold text-white mb-3">Key Locations We Cover in {area.name}</h3>
            <p className="text-gray-400">{area.landmarks}</p>
          </div>

          <div className="text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Other Areas We Serve</h3>
            <OtherAreas currentArea={area.name} />
          </div>
        </div>
      </section>

      <section className="py-16 bg-red-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Need Towing in {area.name}?</h2>
          <p className="text-white/90 text-lg mb-6">We're available 24/7 with fast response times to {area.name} and surrounding areas.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19162343923"><button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-md transition-all inline-flex items-center gap-2"><Phone className="w-5 h-5" /> Call Now</button></a>
            <Link to="/quote"><button className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold rounded-md transition-all">Get Free Quote</button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ServiceAreaPage;
